<template>
  <div class="v-list" ref="scroller" @scroll.passive="onScroll">
    <div class="v-spacer" :style="{ height: totalHeight + 'px' }">
      <div class="v-window" :style="windowStyle">
        <div
          v-for="it in visibleItems"
          :key="it.index"
          class="v-cell"
          :style="{ height: itemHeight + 'px' }"
        >
          <slot :item="it.item" :index="it.index"></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * 轻量定高虚拟列表：只渲染可视区 + 上下缓冲的少量行，
 * 让上万条数据（如超长小说目录）也不卡。零依赖。
 * 用法：父层给一个确定高度的容器，把本组件塞进去（height:100% 撑满）。
 */
export default {
  name: 'VirtualList',
  props: {
    items: { type: Array, default: () => [] },
    itemHeight: { type: Number, default: 44 }, // 单行（单个单元格）高度，必须与渲染一致
    columns: { type: Number, default: 1 },      // 每行列数（详情页 2 列）
    gap: { type: Number, default: 0 },           // 多列时的列间距(px)
    buffer: { type: Number, default: 6 }         // 可视区上下额外渲染的行数
  },
  data() {
    return {
      scrollTop: 0,
      viewportHeight: 0,
      _raf: null
    }
  },
  computed: {
    rowCount() {
      return Math.ceil(this.items.length / this.columns)
    },
    totalHeight() {
      return this.rowCount * this.itemHeight
    },
    startRow() {
      return Math.max(0, Math.floor(this.scrollTop / this.itemHeight) - this.buffer)
    },
    endRow() {
      const visibleRows = Math.ceil((this.viewportHeight || 0) / this.itemHeight)
      return Math.min(this.rowCount, this.startRow + visibleRows + this.buffer * 2)
    },
    offsetY() {
      return this.startRow * this.itemHeight
    },
    windowStyle() {
      const s = {
        transform: `translateY(${this.offsetY}px)`
      }
      if (this.columns > 1) {
        s.display = 'grid'
        s.gridTemplateColumns = `repeat(${this.columns}, 1fr)`
        if (this.gap) s.columnGap = `${this.gap}px`
      }
      return s
    },
    visibleItems() {
      const from = this.startRow * this.columns
      const to = Math.min(this.items.length, this.endRow * this.columns)
      const arr = []
      for (let i = from; i < to; i++) {
        arr.push({ item: this.items[i], index: i })
      }
      return arr
    },
    // 当前可视窗口覆盖的数据下标区间 [start, end)，供父层按需分页加载
    visibleRange() {
      return {
        start: this.startRow * this.columns,
        end: Math.min(this.items.length, this.endRow * this.columns)
      }
    }
  },
  watch: {
    items() {
      // 数据变化后重新测量并回到顶部逻辑由父层决定，这里仅刷新视口
      this.$nextTick(this.measure)
    },
    visibleRange(r) {
      // 可视区间变化时通知父层（用于懒加载缺失数据）
      this.$emit('range', r)
    }
  },
  mounted() {
    this.measure()
    // 首屏也通知一次，父层据此加载第一页
    this.$nextTick(() => this.$emit('range', this.visibleRange))
    window.addEventListener('resize', this.measure)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.measure)
    if (this._raf) cancelAnimationFrame(this._raf)
  },
  methods: {
    measure() {
      const el = this.$refs.scroller
      if (el) {
        this.viewportHeight = el.clientHeight
        this.scrollTop = el.scrollTop
      }
    },
    onScroll() {
      if (this._raf) return
      this._raf = requestAnimationFrame(() => {
        const el = this.$refs.scroller
        if (el) this.scrollTop = el.scrollTop
        this._raf = null
      })
    },
    // 供父层调用：把某序号项滚动到可视区（如定位当前章）
    scrollToIndex(index, align = 'center') {
      const el = this.$refs.scroller
      if (!el || index < 0) return
      const row = Math.floor(index / this.columns)
      let top = row * this.itemHeight
      if (align === 'center') {
        top = top - (el.clientHeight - this.itemHeight) / 2
      }
      el.scrollTop = Math.max(0, top)
      this.measure()
    }
  }
}
</script>

<style lang="scss" scoped>
.v-list {
  height: 100%;
  overflow-y: auto;
  position: relative;
}
.v-spacer {
  position: relative;
  width: 100%;
}
.v-window {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
}
.v-cell {
  box-sizing: border-box;
  overflow: hidden;
}
</style>
